import React, { useState, useEffect, useCallback } from "react";
import styles from "../../../../styles/LmsForm.module.css";
import closeIcon from "../../../../assets/close.png";
import { notification, Select, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { db, storage } from "../../../../firebase-config";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDocs,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import ButtonComponent from "../../../CssComponents/ButtonComponent/ButtonComponent";
import MediaPreviewModal from "../../../CssComponents/MediaPreviewModal/MediaPreviewModal";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // Quill styles
import { Switch } from "antd";

function FormPackage({
  initialValues,
  setInitialValues,
  fetchPackages,
  showForm,
  onClose,
}) {
  const [batchOptions, setBatchOptions] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);

  const [bannerFile, setBannerFile] = useState(null);
  const [bannerFileList, setBannerFileList] = useState([]);

  const [previewFile, setPreviewFile] = useState({ url: "", type: "" });
  const [previewVisible, setPreviewVisible] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchBatches = async () => {
      const querySnapshot = await getDocs(collection(db, "lms_batch"));
      const options = querySnapshot.docs.map((doc) => ({
        label: doc.data().batchName,
        value: doc.data().batchId,
      }));
      setBatchOptions(options);
    };
    const fetchCourses = async () => {
      const querySnapshot = await getDocs(collection(db, "lms_course"));
      const options = querySnapshot.docs.map((doc) => ({
        label: doc.data().courseName,
        value: doc.data().courseId,
      }));
      setCourseOptions(options);
    };
    fetchCourses();
    fetchBatches();
  }, []);

  useEffect(() => {
    // Initialize file lists for existing package
    if (initialValues.packageBanner) {
      setBannerFileList([
        {
          uid: initialValues.packageId || "-1",
          // puttting name from initialValues will reset the fileList
          name: "existing_banner",
          status: "done",
          url: initialValues.packageBanner,
        },
      ]);
    }

    // Cleanup
    return () => {
      setBannerFileList([]);
    };
  }, [initialValues.packageBanner, initialValues.packageId]);

  // Updated handlePreview function
  const handlePreview = useCallback(async (file) => {
    const fileUrl = file.url || file.thumbUrl;
    if (fileUrl) {
      const fileType =
        file.type ||
        (file.name ? file.name.split(".").pop().toLowerCase() : "");
      let previewType = "image";
      if (
        fileType.includes("video") ||
        fileUrl.includes(".mp4") ||
        fileUrl.includes(".webm")
      ) {
        previewType = "video";
      } else if (fileType.includes("pdf") || fileUrl.includes(".pdf")) {
        previewType = "pdf";
      }
      setPreviewFile({ url: fileUrl, type: previewType });
      setPreviewVisible(true);
    }
  }, []);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setInitialValues((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    [setInitialValues]
  );

  const handleBannerChange = ({ fileList }) => {
    setBannerFileList(fileList);
    const file = fileList.length > 0 ? fileList[0].originFileObj : null;
    console.log("Banner file selected:", file);
    setBannerFile(file);
  };

  const validateFile = (file, type) => {
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (!file) return false;
    if (file.size > maxSize) {
      notification.error({
        message: "File too large",
        description: "File must be smaller than 100MB",
      });
      return false;
    }
    return true;
  };

  const uploadToFirebaseStorage = async (file, path) => {
    try {
      if (!validateFile(file, path)) {
        throw new Error("Invalid file");
      }

      const storageRef = ref(storage, `${path}/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      return downloadURL;
    } catch (error) {
      console.error("Upload error:", error);
      throw new Error("Failed to upload file");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let bannerUrl = initialValues.packageBanner || "";

      const isUpdate = !!initialValues.packageId;

      console.log("Banner file state:", bannerFile);

      if (bannerFile && bannerFile instanceof File) {
        bannerUrl = await uploadToFirebaseStorage(bannerFile, "lms_package");
      }

      const packageData = {
        ...initialValues,
        packageBanner: bannerUrl,
        updatedAt: new Date().toISOString(),
      };

      if (isUpdate) {
        await updateDoc(
          doc(db, "lms_package", initialValues.packageId),
          packageData
        );
        await fetchPackages();
        notification.success({
          message: "Package updated successfully.",
          placement: "topRight",
          duration: 3,
        });
      } else {
        // first create empty document with Id only
        const docRef = await addDoc(collection(db, "lms_package"), packageData);
        await updateDoc(doc(db, "lms_package", docRef.id), {
          packageId: docRef.id,
          createdAt: new Date().toISOString(),
        });
        notification.success({
          message: "Package Created",
          description: "New package created successfully.",
          placement: "topRight",
          duration: 3,
        });
      }

      onClose();
      setInitialValues({});
      setBannerFile(null);
      setBannerFileList([]);
    } catch (error) {
      console.error("Error saving package:", error);
      notification.error({
        message: "Error",
        description:
          error.message || "Failed to save package. Please try again.",
        placement: "topRight",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={
        showForm
          ? `${styles.modal} ${styles.displayBlock}`
          : `${styles.modal} ${styles.displayNone}`
      }
    >
      <div className={styles.modalMain}>
        <button
          className={styles.closeBtn}
          onClick={onClose}
          disabled={isLoading}
        >
          <img src={closeIcon} alt="Close" />
        </button>
        <div className={styles.mainContent}>
          <div className={styles.title}>Package</div>
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.labelInputWrapper}>
              <div className={styles.labelText}>Package Name</div>
              <input
                name="packageName"
                className={styles.input}
                value={initialValues.packageName || ""}
                onChange={handleChange}
                placeholder="Enter Package Name"
                required
                disabled={isLoading}
              />
            </div>

            <div className={styles.labelInputWrapper}>
              <div className={styles.labelText}>Package Type</div>
              <Select
                required
                name="packageType"
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Select Package Type"
                value={initialValues.packageType || ""}
                onChange={(value) =>
                  setInitialValues((prev) => ({
                    ...prev,
                    packageType: value ?? "",
                  }))
                }
                options={[
                  { label: "Digital Product", value: "package_dp" },
                  { label: "Live Classes", value: "package_lc" },
                ]}
              />
            </div>

            {initialValues.packageType === "package_lc" && (
              <div className={styles.labelInputWrapper}>
                <div className={styles.labelText}>Select Batches</div>
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select batches"
                  value={initialValues.packageContainingBatches || []}
                  onChange={(value) =>
                    setInitialValues((prev) => ({
                      ...prev,
                      packageContainingBatches: value,
                    }))
                  }
                  options={batchOptions}
                />
              </div>
            )}

            {initialValues.packageType === "package_dp" && (
              <div className={styles.labelInputWrapper}>
                <div className={styles.labelText}>Select Course</div>
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select Courses"
                  value={initialValues.packageContainingCourses || []}
                  onChange={(value) =>
                    setInitialValues((prev) => ({
                      ...prev,
                      packageContainingCourses: value,
                    }))
                  }
                  options={courseOptions}
                />
              </div>
            )}
            {initialValues.packageType === "package_dp" && (
              <div className={styles.labelInputWrapper}>
                <div className={styles.labelText}>Package Price</div>
                <input
                  name="packagePrice"
                  type="number"
                  className={styles.input}
                  value={initialValues.packagePrice}
                  onChange={handleChange}
                  placeholder="Enter Package Price"
                  required
                  disabled={isLoading}
                />
              </div>
            )}

            {initialValues.packageType === "package_lc" && (
              <div className={styles.labelInputWrapper}>
                <div className={styles.labelText}>Registration Price</div>
                <input
                  name="packagePrice"
                  type="number"
                  className={styles.input}
                  value={initialValues.packagePrice}
                  onChange={handleChange}
                  placeholder="Enter Registration Price"
                  required
                  disabled={isLoading}
                />
              </div>
            )}

            <div className={styles.labelInputWrapper}>
              <div className={styles.labelText}>Package Description</div>
              <ReactQuill
                theme="snow"
                value={initialValues.packageDescription || ""}
                onChange={(value) =>
                  handleChange({
                    target: { name: "packageDescription", value },
                  })
                }
                placeholder="Enter Package Description"
                readOnly={isLoading}
              />
            </div>

            <div className={styles.labelInputWrapper}>
              <div className={styles.labelText}>Total Students</div>
              <input
                name="totalStudents"
                type="number"
                className={styles.input}
                value={initialValues.totalStudents || ""}
                onChange={handleChange}
                placeholder="Enter Total Students (e.g., 65)"
                disabled={isLoading}
              />
            </div>

            <div className={styles.labelInputWrapper}>
              <div className={styles.labelText}>Course Duration</div>
              <input
                name="packageDuration"
                className={styles.input}
                value={initialValues.packageDuration || ""}
                onChange={handleChange}
                placeholder="Enter Duration (e.g., 6 Weeks)"
                disabled={isLoading}
              />
            </div>

            <div className={styles.labelInputWrapper}>
              <div className={styles.labelText}>What You'll Learn</div>
              <ReactQuill
                theme="snow"
                value={initialValues.learningPoints || ""}
                onChange={(value) =>
                  handleChange({ target: { name: "learningPoints", value } })
                }
                placeholder="Enter learning points (use bullet points)"
                readOnly={isLoading}
                modules={{
                  toolbar: [
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["bold", "italic"],
                    ["clean"],
                  ],
                }}
              />
            </div>

            <div className={styles.labelInputWrapper}>
              <div className={styles.labelText}>Package Difficulty</div>
              <Select
                name="packageDifficulty"
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Select Difficulty Level"
                value={initialValues.packageDifficulty || ""}
                onChange={(value) =>
                  setInitialValues((prev) => ({
                    ...prev,
                    packageDifficulty: value ?? "",
                  }))
                }
                options={[
                  { label: "Beginner", value: "Beginner" },
                  { label: "Intermediate", value: "Intermediate" },
                  { label: "Advanced", value: "Advanced" },
                ]}
                disabled={isLoading}
              />
            </div>

            <div className={styles.labelInputWrapper}>
              <div className={styles.labelText}>Package Banner (Image)</div>
              <Upload
                accept="image/*"
                listType="picture-card"
                beforeUpload={() => false}
                fileList={bannerFileList}
                onChange={handleBannerChange}
                onPreview={handlePreview}
                showUploadList={{ showRemoveIcon: true }}
                maxCount={1}
                disabled={isLoading}
              >
                {bannerFileList.length < 1 && (
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>
                      Upload <br /> Banner
                    </div>
                  </div>
                )}
              </Upload>
            </div>

            <div className={styles.labelInputWrapper}>
              <div className={styles.labelText}>
                {initialValues.packageStatus
                  ? "Package Active"
                  : "Package Inactive"}
              </div>
              <Switch
                style={{
                  maxWidth: "50px",
                }}
                checked={initialValues.packageStatus || false}
                onChange={(checked) =>
                  setInitialValues((prev) => ({
                    ...prev,
                    packageStatus: checked,
                  }))
                }
                disabled={isLoading}
              />
            </div>

            <ButtonComponent
              buttonText={
                initialValues.packageId ? "Update Package" : "Save Package"
              }
              buttonType="submit"
              disabled={isLoading}
              loading={isLoading}
            />
          </form>
        </div>
      </div>

      <MediaPreviewModal
        previewVisible={previewVisible}
        setPreviewVisible={setPreviewVisible}
        previewFile={previewFile}
      />
    </div>
  );
}

export default FormPackage;
